import { useEffect, useRef, useState, useCallback } from "react";
import { backgroundStreamManager, StreamState } from "@/services/backgroundStreamManager";
import { MessageType } from "@/components/common/copilot/types";
import { ChatDataConfig } from "@/screens/creatives/types";
import { useGlobalStreams } from "./useGlobalStreams";

interface UseBackgroundStreamOptions {
  accountId: string;
  reportId: string;
  chatId: string;
  onStreamStart?: () => void;
  onStreamUpdate?: (chatId?: string) => void;
  onStreamComplete?: (chatId: string) => void;
  onStreamError?: (error: string) => void;
}

export function useBackgroundStream({
  accountId,
  reportId,
  chatId,
  onStreamStart,
  onStreamUpdate,
  onStreamComplete,
  onStreamError,
}: UseBackgroundStreamOptions) {
  const [activeStreams, setActiveStreams] = useState<Map<string, StreamState>>(new Map());
  const streamIdRef = useRef<string | null>(null);
  const listenerRef = useRef<((stream: StreamState) => void) | null>(null);
  const { streams: globalStreams }: { streams: StreamState[] } = useGlobalStreams();
  const listenerRegistry = useRef<Map<string, (stream: StreamState) => void>>(new Map());

  // Generate unique stream ID
  const generateStreamId = useCallback(() => {
    return `stream_${accountId}_${reportId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, [accountId, reportId]);

  // Sync local state with global state
  useEffect(() => {
    const streamsMap = new Map<string, StreamState>();
    globalStreams.forEach((stream) => {
      streamsMap.set(stream.id, stream);
    });
    setActiveStreams(streamsMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalStreams.length]);

  // Start a new background stream
  const startStream = useCallback(
    async (
      data: ChatDataConfig,
      initialMessages: MessageType[] = [],
    ): Promise<string> => {
      const streamId = generateStreamId();
      streamIdRef.current = streamId;

      const onStart = () => {
        onStreamStart?.();
      };

      const onUpdate = (
        chatIdStream?: string,
        messages?: MessageType[],
        status?: string,
        error?: string | null,
      ) => {
        // The global state will be updated by the background stream manager
        // This callback is just for component-specific updates
        if (!chatId && chatIdStream) {
          onStreamUpdate?.(chatIdStream);
        }
        if (error) {
          onStreamError?.(error);
        }
      };

      const onComplete = (chatId: string) => {
        onStreamComplete?.(chatId);
      };

      // The background stream manager will handle adding the stream to global state

      // Start the background stream
      await backgroundStreamManager.startStream(
        streamId,
        accountId,
        reportId,
        data,
        onUpdate,
        onComplete,
        initialMessages,
        onStart,
      );

      return streamId;
    },
    [
      generateStreamId,
      accountId,
      reportId,
      onStreamStart,
      chatId,
      onStreamUpdate,
      onStreamError,
      onStreamComplete,
    ],
  );

  // Cancel current stream
  const cancelCurrentStream = useCallback(() => {
    if (streamIdRef.current) {
      backgroundStreamManager.cancelStream(streamIdRef.current);
      streamIdRef.current = null;
    }
  }, []);

  // Get current stream state
  const getCurrentStreamState = useCallback((): StreamState | undefined => {
    if (streamIdRef.current) {
      return activeStreams.get(streamIdRef.current);
    }
    return undefined;
  }, [activeStreams]);

  // Check if current stream is active
  const isCurrentStreamActive = useCallback((): boolean => {
    return streamIdRef.current !== null && activeStreams.has(streamIdRef.current);
  }, [activeStreams]);

  // Get stream by ID
  const getStreamById = useCallback(
    (streamId: string): StreamState | undefined => {
      return activeStreams.get(streamId);
    },
    [activeStreams],
  );

  // Check if there's an active stream for a specific chat
  const hasActiveStreamForChat = useCallback(
    (chatId: string): boolean => {
      return Array.from(activeStreams.values()).some(
        (stream) =>
          stream.chatId === chatId &&
          (stream.status === "loading" || stream.status === "in_progress"),
      );
    },
    [activeStreams],
  );

  // Subscribe to an existing stream by chat ID
  const subscribeToExistingStream = useCallback(
    (
      chatId: string,
      onStreamUpdate?: (stream: StreamState) => void,
    ): StreamState | undefined => {
      // Find streams for the exact chat ID
      const existingStream = Array.from(activeStreams.values()).find(
        (stream) =>
          stream.chatId === chatId &&
          (stream.status === "loading" || stream.status === "in_progress"),
      );

      if (existingStream) {
        console.log("Found existing stream for chat:", chatId);

        // Set this as the current stream if it's different
        if (streamIdRef.current !== existingStream.id) {
          streamIdRef.current = existingStream.id;
        }

        // Add our listener to the existing stream with chat ID validation
        if (onStreamUpdate) {
          const chatValidatedListener = (stream: StreamState) => {
            // Only call the update if the stream belongs to the requested chat
            try {
              onStreamUpdate(stream);
            } catch (error) {
              console.error("Error in stream listener:", error);
            }
          };
          backgroundStreamManager.addListener(existingStream.id, chatValidatedListener);

          // Store the listener with the stream ID for cleanup
          listenerRegistry.current.set(existingStream.id, chatValidatedListener);
        }

        return existingStream;
      } else {
        // Clear current stream reference if navigating to a different chat
        if (streamIdRef.current) {
          const currentStream = activeStreams.get(streamIdRef.current);
          if (!currentStream || currentStream.chatId !== chatId) {
            streamIdRef.current = null;
          }
        }
      }

      return undefined;
    },
    [activeStreams],
  );

  // Unsubscribe from a stream
  const unsubscribeFromStream = useCallback(
    (streamId: string, onStreamUpdate: (stream: StreamState) => void): void => {
      backgroundStreamManager.removeListener(streamId, onStreamUpdate);
    },
    [],
  );

  // Listen to stream updates
  useEffect(() => {
    if (streamIdRef.current) {
      listenerRef.current = (stream: StreamState) => {
        // Handle errors
        if (stream.status === "error" && stream.error) {
          onStreamError?.(stream.error);
        }
      };

      backgroundStreamManager.addListener(streamIdRef.current, listenerRef.current);
    }

    return () => {
      if (streamIdRef.current && listenerRef.current) {
        backgroundStreamManager.removeListener(streamIdRef.current, listenerRef.current);
      }
    };
  }, [onStreamError]);

  // // Cleanup on unmount
  // useEffect(() => {
  //   return () => {
  //     if (streamIdRef.current) {
  //       backgroundStreamManager.cancelStream(streamIdRef.current);
  //     }
  //   };
  // }, []);

  // Clean up all listeners for this hook instance
  const cleanupAllListeners = useCallback(() => {
    for (const [streamId, listener] of listenerRegistry.current) {
      backgroundStreamManager.removeListener(streamId, listener);
    }
    listenerRegistry.current.clear();
    streamIdRef.current = null;
  }, []);

  // // Cleanup on unmount
  // useEffect(() => {
  //   return () => {
  //     cleanupAllListeners();
  //   };
  // }, [cleanupAllListeners]);

  return {
    startStream,
    cancelCurrentStream,
    getCurrentStreamState,
    isCurrentStreamActive,
    getStreamById,
    hasActiveStreamForChat,
    subscribeToExistingStream,
    unsubscribeFromStream,
    cleanupAllListeners,
    activeStreams: Array.from(activeStreams.values()),
  };
}
